// Every map in the app is created through this module, so the MapLibre
// specifics live in one place: loading the library on demand, converting
// coordinates and falling back when a map cannot be shown. Coordinates cross this module's boundary as { lat, lng }; MapLibre's
// [lng, lat] order is produced only by toLngLat.

import maplibrePackage from "maplibre-gl/package.json";

// A style that has not arrived by then is treated as unreachable, since an
// overloaded provider tends to hang rather than fail.
const STYLE_TIMEOUT_MS = 15000;

const LOCALE = {
  "AttributionControl.ToggleAttribution": "Mostrar/ocultar atribuição",
  "Map.Title": "Mapa",
  "Marker.Title": "Marcador",
  "NavigationControl.ZoomIn": "Aproximar",
  "NavigationControl.ZoomOut": "Afastar",
  "Popup.Close": "Fechar",
  "CooperativeGesturesHandler.WindowsHelpText": "Use Ctrl + rolagem para aproximar o mapa",
  "CooperativeGesturesHandler.MacHelpText": "Use ⌘ + rolagem para aproximar o mapa",
  "CooperativeGesturesHandler.MobileHelpText": "Use dois dedos para mover o mapa",
};

let maplibrePromise = null;
let downloadFailed = false;
let failedDownloads = 0;

// Imported on first use rather than from application.js, so pages without a
// map never download it. The library is imported natively from the copy that
// config/webpack/webpack.config.js places next to the packs (that file says why
// it is not bundled); only its stylesheet goes through webpack. Should
// WEBPACKER_ASSET_HOST ever move the packs to another host, that host must send
// CORS headers, and the worker then needs blob: in the CSP.
//
// A failed download stays failed, so that the maps of one attempt to show a
// map share it, until retryFailedDownload is called.
export function loadMapLibre() {
  // A browser remembers a failed module download for as long as the page is
  // open, and fails every later import of that URL without trying again. So
  // the retry loads a second copy of the files, from retry/. A query string
  // would not do: it renews maplibre-gl.mjs but not maplibre-gl-shared.mjs,
  // which that file imports by a fixed relative path. The copy's imports, and
  // the worker MapLibre looks for next to itself, all resolve inside retry/.
  // There is one such copy: should the retry fail too, the maps show the
  // fallback until the page reloads.
  const folder = failedDownloads ? "retry/" : "";
  const url = `${__webpack_public_path__}maplibre-gl-${maplibrePackage.version}/${folder}maplibre-gl.mjs`;

  maplibrePromise ||= Promise.all([
    import(/* webpackIgnore: true */ url),
    import(/* webpackChunkName: "maplibre" */ "maplibre-gl/dist/maplibre-gl.css"),
  ]).then(([maplibregl]) => maplibregl).catch((error) => {
    downloadFailed = true;
    failedDownloads += 1;
    throw error;
  });

  return maplibrePromise;
}

// Lets the next loadMapLibre try again after a failed download. Called when
// the user asks for a map anew, as by reopening a location modal, rather than
// by each map: a modal's prefetch and its map would otherwise spend the one
// retry a moment apart, while the network is still down.
export function retryFailedDownload() {
  if (!downloadFailed) return;

  downloadFailed = false;
  maplibrePromise = null;
}

// Resolves with the map once its style has loaded, when sources and layers can
// be added, or with null once the fallback has been rendered in its place.
// `center` is { lat, lng }; `bounds` is a pair of them (south-west, north-east).
export async function createMap(container, { center, zoom, bounds, ...options } = {}) {
  // A container can be reused, as a modal is reopened, and may still hold the
  // fallback from a previous failed attempt.
  container.replaceChildren();
  container.dataset.mapState = "loading";

  let maplibregl;
  let map;
  try {
    maplibregl = await loadMapLibre();
    map = new maplibregl.Map({
      container,
      style: styleUrl(),
      locale: LOCALE,
      // As on Google's maps: no rotation or tilt, which only disorient here.
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
      // Left out when absent: an explicit undefined would override defaults.
      ...(center && { center: toLngLat(center) }),
      ...(zoom !== undefined && { zoom }),
      ...(bounds && { bounds: bounds.map(toLngLat) }),
      ...options,
    });
  } catch (error) {
    // The chunk failed to download, there is no WebGL2
    // (GPUInitializationError), or the coordinates were not numbers.
    console.error(error);
    renderFallback(container, center);
    return null;
  }

  map.touchZoomRotate.disableRotation();
  map.addControl(new maplibregl.NavigationControl({ showCompass: false }));

  return new Promise((resolve) => {
    const fail = (error) => {
      clearTimeout(timeout);
      map.off("error", onError);
      console.error("The map style could not be loaded", error);
      map.remove();
      renderFallback(container, center);
      resolve(null);
    };
    // An error before the style has loaded means the style itself could not be
    // fetched, as in a provider outage. Later ones (a tile or glyph that failed)
    // leave a usable map, and MapLibre logs those to the console by itself.
    const onError = (event) => fail(event.error);
    const timeout = setTimeout(
      () => fail(new Error(`No style after ${STYLE_TIMEOUT_MS} ms`)),
      STYLE_TIMEOUT_MS
    );

    map.on("error", onError);
    map.once("style.load", () => {
      clearTimeout(timeout);
      map.off("error", onError);
      container.dataset.mapState = "loaded";
      resolve(map);
    });
  });
}

// Accepts strings too, since coordinates usually come from data attributes.
// parseFloat rather than Number: Number("") is 0, which would quietly place a
// blank coordinate off the coast of Africa instead of failing.
export function toLngLat({ lat, lng }) {
  return [parseFloat(lng), parseFloat(lat)];
}

// Google Maps URLs need no key and are free. They keep Street View and satellite
// imagery, which the MapLibre maps do not have, one click away.
export function googleMapsUrl({ lat, lng }) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
}

function styleUrl() {
  const url = document.querySelector('meta[name="map-style-url"]')?.content;
  if (!url) throw new Error('Missing <meta name="map-style-url">');

  return url;
}

function renderFallback(container, coordinates) {
  const fallback = document.createElement("div");
  fallback.className = "text-center text-muted";
  Object.assign(fallback.style, {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  });

  const message = document.createElement("p");
  message.textContent = "Não foi possível exibir o mapa.";
  fallback.append(message);

  if (coordinates && toLngLat(coordinates).every(Number.isFinite)) {
    const link = document.createElement("a");
    link.href = googleMapsUrl(coordinates);
    link.target = "_blank";
    link.rel = "noopener";
    link.textContent = "Abrir no Google Maps";
    fallback.append(link);
  }

  container.replaceChildren(fallback);
  container.dataset.mapState = "failed";
}
